import React, { useState, useEffect, useCallback } from 'react';
import Icon from 'components/AppIcon';
import * as sessionsApi from 'shared/api/auth/sessionsApi';

const ConnectedDevices = ({ onUnsavedChanges, hasUnsavedChanges }) => {
  const [devices, setDevices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(null);

  // Fetch sessions from API
  const fetchDevices = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const sessions = await sessionsApi.getSessions();
      const transformedDevices = sessions.map((session, index) => ({
        id: session.sessionId,
        name: parseDeviceName(session.userAgent),
        type: detectDeviceType(session.userAgent),
        os: parseOS(session.userAgent),
        browser: parseBrowser(session.userAgent),
        location: 'Неизвестно', // API не возвращает location
        ipAddress: session.ipAddress || 'Unknown',
        lastActive: session.createdAt,
        isCurrent: index === 0, // Первая сессия - текущая (или можно сравнивать с refresh token)
        loginTime: formatDateTime(session.createdAt),
        expiresAt: session.expiresAt
      }));
      setDevices(transformedDevices);
    } catch (err) {
      console.error('Failed to fetch devices:', err);
      setError('Не удалось загрузить список устройств');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  // Format datetime
  const formatDateTime = (dateString) => {
    if (!dateString) return 'Неизвестно';
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Helper functions for parsing user agent
  const detectDeviceType = (userAgent) => {
    if (!userAgent) return 'desktop';
    if (/mobile|iphone|android/i.test(userAgent) && !/tablet|ipad/i.test(userAgent)) return 'mobile';
    if (/tablet|ipad/i.test(userAgent)) return 'tablet';
    return 'desktop';
  };

  const parseDeviceName = (userAgent) => {
    if (!userAgent) return 'Unknown Device';
    const browser = parseBrowser(userAgent);
    const os = parseOS(userAgent);
    return `${browser} на ${os}`;
  };

  const parseOS = (userAgent) => {
    if (!userAgent) return 'Unknown';
    if (/windows nt 10/i.test(userAgent)) return 'Windows 10/11';
    if (/windows/i.test(userAgent)) return 'Windows';
    if (/macintosh|mac os/i.test(userAgent)) return 'macOS';
    if (/iphone/i.test(userAgent)) return 'iOS';
    if (/android/i.test(userAgent)) return 'Android';
    if (/linux|ubuntu/i.test(userAgent)) return 'Linux';
    return 'Unknown';
  };

  const parseBrowser = (userAgent) => {
    if (!userAgent) return 'Browser';
    if (/edg/i.test(userAgent)) return 'Edge';
    if (/chrome/i.test(userAgent) && !/edg/i.test(userAgent)) return 'Chrome';
    if (/firefox/i.test(userAgent)) return 'Firefox';
    if (/safari/i.test(userAgent) && !/chrome/i.test(userAgent)) return 'Safari';
    if (/opera|opr/i.test(userAgent)) return 'Opera';
    return 'Browser';
  };

  const getDeviceIcon = (type) => {
    switch (type) {
      case 'mobile': return 'Smartphone';
      case 'tablet': return 'Tablet';
      case 'desktop': return 'Monitor';
      default: return 'Monitor';
    }
  };

  const getOSIcon = (os) => {
    if (os.includes('Windows')) return 'Monitor';
    if (os.includes('iOS')) return 'Smartphone';
    if (os.includes('Android')) return 'Smartphone';
    if (os.includes('Ubuntu') || os.includes('Linux')) return 'Terminal';
    if (os.includes('macOS')) return 'Monitor';
    return 'Monitor';
  };

  const handleLogoutDevice = async (deviceId) => {
    try {
      await sessionsApi.terminateSession(deviceId);
      setDevices(devices.filter(device => device.id !== deviceId));
      setShowLogoutConfirm(null);
    } catch (err) {
      console.error('Failed to logout device:', err);
      setError('Не удалось завершить сессию');
    }
  };

  const handleLogoutAllDevices = async () => {
    if (window.confirm('Вы уверены, что хотите завершить все сессии? Вам потребуется войти в систему заново на всех устройствах.')) {
      try {
        await sessionsApi.terminateAllSessions();
        setDevices(devices.filter(device => device.isCurrent));
      } catch (err) {
        console.error('Failed to logout all devices:', err);
        setError('Не удалось завершить сессии');
      }
    }
  };

  const formatLastActive = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Менее часа назад';
    if (diffInHours < 24) return `${diffInHours} ч. назад`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays === 1) return 'Вчера';
    if (diffInDays < 7) return `${diffInDays} дн. назад`;
    
    return date.toLocaleDateString('ru-RU');
  };

  const getLocationFlag = (location) => {
    if (location.includes('Москва')) return '🇷🇺';
    if (location.includes('Санкт-Петербург')) return '🇷🇺';
    if (location.includes('Екатеринбург')) return '🇷🇺';
    return '🌍';
  };

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[300px]">
        <div className="text-center">
          <Icon name="Loader2" size={32} className="text-primary animate-spin mx-auto mb-4" />
          <p className="text-text-secondary">Загрузка устройств...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Error Alert */}
      {error && (
        <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-md flex items-center justify-between">
          <p className="text-sm text-error">{error}</p>
          <button onClick={() => setError('')} className="text-error hover:text-error-700">
            <Icon name="X" size={16} />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-text-primary">
            Подключенные устройства
          </h2>
          <p className="text-text-secondary mt-1">
            Управляйте активными сессиями и устройствами с доступом к вашему аккаунту
          </p>
        </div>
        <button
          onClick={handleLogoutAllDevices}
          disabled={devices.length <= 1}
          className={`
            flex items-center gap-2 px-4 py-2 border rounded-md transition-colors duration-150
            focus:outline-none focus:ring-2 focus:ring-error/20
            ${devices.length <= 1 
              ? 'border-secondary-300 text-secondary-400 cursor-not-allowed'
              : 'border-error text-error hover:bg-error-50 hover-lift'
            }
          `}
        >
          <Icon name="LogOut" size={16} />
          Завершить все сессии
        </button>
      </div>

      {/* Security Notice */}
      <div className="border rounded-lg p-4 mb-6 border-border">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-accent mt-0.5" />
          <div>
            <h3 className="font-medium text-accent-700 mb-1">
              Безопасность аккаунта
            </h3>
            <p className="text-sm text-accent-600">
              Регулярно проверяйте список подключенных устройств. Если вы видите незнакомое устройство, 
              немедленно завершите его сессию и измените пароль.
            </p>
          </div>
        </div>
      </div>

      {/* Devices List */}
      <div className="space-y-4">
        {devices.map(device => (
          <div
            key={device.id}
            className={`
              bg-surface border rounded-lg p-6 transition-all duration-150
              ${device.isCurrent 
                ? 'border-primary bg-primary-50/30' :'border-secondary-200 hover:border-secondary-300 hover:shadow-sm'
              }
            `}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className={`
                  p-3 rounded-lg
                  ${device.isCurrent ? 'bg-primary text-white' : 'bg-secondary-100 text-secondary-600'}
                `}>
                  <Icon name={getDeviceIcon(device.type)} size={24} />
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-text-primary">
                      {device.name}
                    </h3>
                    {device.isCurrent && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-success text-white text-xs font-medium rounded-full">
                        <Icon name="CheckCircle" size={12} />
                        Текущая сессия
                      </span>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-text-secondary">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Icon name={getOSIcon(device.os)} size={14} />
                        <span>{device.os}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Icon name="Globe" size={14} />
                        <span>{device.browser}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span>{getLocationFlag(device.location)}</span>
                        <span>{device.location}</span>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Icon name="Wifi" size={14} />
                        <span>{device.ipAddress}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Icon name="Clock" size={14} />
                        <span>Вход: {device.loginTime}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Icon name="Activity" size={14} />
                        <span>Активность: {formatLastActive(device.lastActive)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {!device.isCurrent && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowLogoutConfirm(device.id)}
                    className="
                      p-2 text-error hover:bg-error-50 rounded-md transition-colors duration-150
                      focus:outline-none focus:ring-2 focus:ring-error/20
                    "
                    title="Завершить сессию"
                  >
                    <Icon name="LogOut" size={16} />
                  </button>
                </div>
              )}
            </div>

            {/* Security Status */}
            <div className="mt-4 pt-4 border-t border-secondary-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 text-sm">
                  <div className={`
                    flex items-center gap-1
                    ${device.location.includes('Москва') ? 'text-success' : 'text-warning'}
                  `}>
                    <Icon name="MapPin" size={14} />
                    <span>
                      {device.location.includes('Москва') ? 'Обычное местоположение' : 'Новое местоположение'}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-1 text-success">
                    <Icon name="Shield" size={14} />
                    <span>Безопасное соединение</span>
                  </div>
                </div>
                
                <span className="text-xs text-text-secondary">
                  ID сессии: {device.id.toString().padStart(8, '0')}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Session Statistics */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-secondary-50 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary rounded-md">
              <Icon name="Monitor" size={20} className="text-white" />
            </div>
            <div>
              <p className="text-sm text-text-secondary">Активные сессии</p>
              <p className="text-xl font-semibold text-text-primary">{devices.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-secondary-50 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-accent rounded-md">
              <Icon name="MapPin" size={20} className="text-white" />
            </div>
            <div>
              <p className="text-sm text-text-secondary">Местоположения</p>
              <p className="text-xl font-semibold text-text-primary">
                {new Set(devices.map(d => d.location.split(',')[0])).size}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-secondary-50 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-success rounded-md">
              <Icon name="Shield" size={20} className="text-white" />
            </div>
            <div>
              <p className="text-sm text-text-secondary">Безопасные соединения</p>
              <p className="text-xl font-semibold text-text-primary">{devices.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-surface rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-warning-100 rounded-md">
                <Icon name="AlertTriangle" size={20} className="text-warning" />
              </div>
              <h3 className="text-lg font-semibold text-text-primary">
                Завершить сессию?
              </h3>
            </div>
            
            <p className="text-text-secondary mb-6">
              Вы уверены, что хотите завершить сессию на этом устройстве? 
              Пользователю потребуется войти в систему заново.
            </p>
            
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowLogoutConfirm(null)}
                className="
                  px-4 py-2 border border-secondary-300 text-text-primary rounded-md
                  hover:bg-secondary-50 transition-colors duration-150
                  focus:outline-none focus:ring-2 focus:ring-primary/20
                "
              >
                Отменить
              </button>
              <button
                onClick={() => handleLogoutDevice(showLogoutConfirm)}
                className="
                  px-4 py-2 bg-error text-white rounded-md
                  hover:bg-error-600 transition-colors duration-150
                  focus:outline-none focus:ring-2 focus:ring-error/20
                  flex items-center gap-2
                "
              >
                <Icon name="LogOut" size={16} />
                Завершить сессию
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConnectedDevices;